import React, { useEffect, useState } from 'react';

import { Button, Typography } from '@material-ui/core';
import { createStyles, WithStyles, withStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

export interface CountDownProps {
  onCancel(progress: number): void;
  onTimeout?(): void;
  text: string;
  timeout?: number;
}

type ProgressClassKey =
  | 'done'
  | 'inProgress'
  | 'progressContainer'
  | 'progressLeft'
  | 'progressRight'
  | 'progressText'
  | 'radialProgress';

type ProgressProps = {
  done: boolean;
  progress: number;
  timeout: number;
};

const Progress = withStyles<ProgressClassKey, {}, ProgressProps>(
  (theme) =>
    createStyles({
      radialProgress: {
        alignItems: 'center',
        backgroundColor: theme.palette.divider,
        borderRadius: '146px',
        display: 'flex',
        fontSize: '60px',
        height: '146px',
        justifyContent: 'center',
        margin: theme.spacing(1),
        overflow: 'hidden',
        position: 'relative',
        width: '146px',
      },
      inProgress: {
        color: theme.palette.primary.main,
      },
      done: {
        color: theme.palette.action.disabled,
      },

      progressText: {
        alignItems: 'center',
        backgroundColor: theme.palette.background.paper,
        borderRadius: '100%',
        display: 'flex',
        height: '138px',
        justifyContent: 'center',
        left: '50%',
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '138px',
        zIndex: 9999,
      },

      progressContainer: {
        height: '146px',
        overflow: 'hidden',
        position: 'absolute',
        top: 0,
        width: '73px',
      },
      progressLeft: {
        left: 0,
        '& > div': {
          animationDuration: (props) => `${props.timeout}s`,
          animationFillMode: 'forwards',
          animationName: '$fd-rotate-bg-left',
          animationTimingFunction: 'linear',
          backgroundColor: theme.palette.primary.main,
          borderRadius: '146px 0 0 146px',
          height: '146px',
          left: 0,
          transformOrigin: '100% 50%',
          width: '73px',
        },
      },
      progressRight: {
        left: '73px',
        '& > div': {
          animationDuration: (props) => `${props.timeout}s`,
          animationFillMode: 'forwards',
          animationName: '$fd-rotate-bg-right',
          animationTimingFunction: 'linear',
          backgroundColor: theme.palette.primary.main,
          borderRadius: '0 146px 146px 0',
          height: '146px',
          transformOrigin: '0 50%',
          width: '73px',
        },
      },
      // MATERIAL-UI BUG KEYFRAMES NOT WORKING INSIDE makeStyles
      '@keyframes fd-rotate-bg-right': {
        '50%': {
          transform: 'rotateZ(180deg)',
        },
        '100%': {
          transform: 'rotateZ(180deg)',
        },
      },
      '@keyframes fd-rotate-bg-left': {
        '0%': {
          transform: 'rotateZ(0)',
        },
        '50%': {
          transform: 'rotateZ(0)',
        },
        '100%': {
          transform: 'rotateZ(180deg)',
        },
      },
    }),
  { name: 'FdProgress' },
)(
  ({
    classes,
    done,
    progress,
  }: ProgressProps & WithStyles<ProgressClassKey>) => (
    <div
      className={clsx(classes.radialProgress, {
        [classes.inProgress]: !done,
        [classes.done]: done,
      })}
    >
      <div className={classes.progressText}>
        <Typography color="inherit" variant="inherit" aria-live="polite">
          {progress}
        </Typography>
      </div>
      <div className={clsx(classes.progressContainer, classes.progressLeft)}>
        <div />
      </div>
      <div className={clsx(classes.progressContainer, classes.progressRight)}>
        <div />
      </div>
    </div>
  ),
);

export const CountDown = (props: CountDownProps): JSX.Element => {
  const { onCancel, onTimeout, text, timeout = 15 } = props;

  const [progress, setProgress] = useState(0);
  const [stop, setStop] = useState(false);

  const done = progress === timeout;

  const timer = React.useRef<NodeJS.Timeout>();

  useEffect(() => {
    timer.current = setInterval(() => {
      setProgress((prevProgress) =>
        prevProgress >= timeout ? timeout : prevProgress + 1,
      );
    }, 1000);

    return () => {
      clearInterval(timer.current);
    };
  }, [timeout]);

  useEffect(() => {
    if (stop || done) {
      clearInterval(timer.current);
    }
  }, [stop, done]);

  useEffect(() => {
    if (done && onTimeout) {
      onTimeout();
    }
  }, [done, onTimeout]);

  return (
    <>
      <Progress done={done} progress={timeout - progress} timeout={timeout} />
      <Button
        aria-label={text}
        disabled={stop || done}
        onClick={() => {
          setStop(true);
          onCancel(progress);
        }}
        variant="text"
      >
        {text}
      </Button>
    </>
  );
};
